/**
 * Utility functions for form validation
 */

/**
 * Validates character input
 * @param {string} character - The character description to validate
 * @returns {Object} - Validation result with isValid and message
 */
export function validateCharacter(character) {
  // Required check
  if (!character || character.trim() === '') {
    return {
      isValid: false,
      message: 'Please describe the main character'
    };
  }
  
  // Length check
  if (character.length < 3) {
    return {
      isValid: false,
      message: 'Character description is too short'
    };
  }
  
  if (character.length > 100) {
    return {
      isValid: false,
      message: 'Character description is too long (max 100 characters)'
    };
  }
  
  // All checks passed
  return {
    isValid: true,
    message: ''
  };
}

/**
 * Validates setting input
 * @param {string} setting - The setting description to validate
 * @returns {Object} - Validation result with isValid and message
 */
export function validateSetting(setting) {
  // Required check
  if (!setting || setting.trim() === '') {
    return {
      isValid: false,
      message: 'Please describe the story setting'
    };
  }
  
  // Length check
  if (setting.length < 3) {
    return {
      isValid: false,
      message: 'Setting description is too short'
    };
  }
  
  if (setting.length > 100) {
    return {
      isValid: false,
      message: 'Setting description is too long (max 100 characters)'
    };
  }
  
  // All checks passed
  return {
    isValid: true,
    message: ''
  };
}

/**
 * Validates age group selection
 * @param {string} ageGroupId - The selected age group ID
 * @returns {Object} - Validation result with isValid and message
 */
export function validateAgeGroup(ageGroupId) {
  // Required check
  if (!ageGroupId) {
    return {
      isValid: false,
      message: 'Please select an age group'
    };
  }
  
  // Valid options check (could import from age-groups.js but avoiding circular dependencies)
  const validAgeGroups = [
    'babies',
    'toddlers', 
    'preschoolers-young', 
    'preschoolers-older',
    'schoolchildren',
    'teenagers'
  ];
  
  if (!validAgeGroups.includes(ageGroupId)) {
    return {
      isValid: false,
      message: 'Please select a valid age group'
    };
  }
  
  // All checks passed
  return {
    isValid: true,
    message: ''
  };
}

/**
 * Validates a complete story form
 * @param {Object} formData - The form data object with character, setting, and ageGroup
 * @returns {Object} - Validation result with isValid and errors object
 */
export function validateStoryForm(formData) {
  const characterValidation = validateCharacter(formData.character);
  const settingValidation = validateSetting(formData.setting);
  const ageGroupValidation = validateAgeGroup(formData.ageGroup);
  
  const isValid = characterValidation.isValid && 
                 settingValidation.isValid && 
                 ageGroupValidation.isValid;
  
  return {
    isValid,
    errors: {
      character: characterValidation.message,
      setting: settingValidation.message,
      ageGroup: ageGroupValidation.message
    }
  };
}

/**
 * Sanitizes user input to prevent injection attacks
 * @param {string} input - The user input to sanitize
 * @returns {string} - Sanitized input
 */
export function sanitizeInput(input) {
  if (!input) return '';
  
  // Replace potentially dangerous HTML/script characters
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .trim();
}
